// BRIEF 91 §3a — WHAT A RUNNING TIMER IS ABOUT, RESOLVED IN ONE PLACE.
//
// A timer row is keyed `(taskId, subtaskId)` and every reader of it — the
// header label, the timer dropdown, the limit dialog, the running-timer
// notification, the compact column's rows and, the one that matters, the
// stop-and-log path — resolved that key the same way:
//
//     const tsk = tasks.find((x) => x.id === tm.taskId);
//
// Six copies of one lookup, and the day a timer's parent is NOT a task all six
// answer `undefined` independently. `openTimerLogTe` is where that costs
// something real: it seeds the time-entry modal from `tsk`, so a timer with no
// task in `tasks` produces an entry with no case, no matter and no description —
// silently, on the money path.
//
// §3a asks for the event subtask row to get the timer the task subtask row has.
// An event subtask HAS no parent task (`Subtask.parentEventId`,
// schema.prisma:1127), so its timer row carries `eventId` instead and this
// function is the one place that knows there are two kinds. Everything else asks
// it what the timer is about and gets the same shape back either way.
//
// IT IS DELIBERATELY TOTAL: `null` is a real answer, meaning "the parent is not
// in this client's lists" — a retracted delegation, a list not yet loaded, a
// stale persisted row. Callers already had to handle that (`tasks.find` could
// always miss); they keep handling exactly it.

export type TimerRow = {
  taskId?: number | null;
  subtaskId?: number | null;
  /* §3a — set INSTEAD of taskId when the timer belongs to an EVENT's subtask.
     The `(taskId, subtaskId)` key is untouched by it: subtask ids come from one
     sequence, so a `(null, S)` row is as unique as a `(T, S)` one and every
     existing comparison in the timer engine keeps working unchanged. */
  eventId?: number | null;
  paused?: boolean;
  elapsed?: number;
  startedAt?: number;
};

type SubtaskLike = { id?: number | null; text?: string | null };
type ParentLike = {
  id?: number | null;
  title?: string | null;
  type?: string | null;
  caseId?: number | null;
  ccId?: number | null;
  subtasks?: SubtaskLike[] | null;
};

export type TimerSubject = {
  kind: "task" | "event";
  /** The task or the agenda event the timer hangs off. */
  parent: ParentLike;
  /** The subtask, when the timer is a subtask timer AND the row carries it. */
  subtask: SubtaskLike | null;
  /** What to show: the subtask's text when there is one, else the parent's title. */
  label: string;
  caseId: number | null;
  ccId: number | null;
  type: string;
};

export function timerSubject(
  tm: TimerRow | null | undefined,
  tasks: readonly ParentLike[] | null | undefined,
  agendaEvents: readonly ParentLike[] | null | undefined,
): TimerSubject | null {
  if (!tm) return null;
  const pick = (
    kind: "task" | "event",
    parent: ParentLike,
  ): TimerSubject => {
    const subtask =
      tm.subtaskId != null
        ? (parent.subtasks || []).find((s) => s && s.id === tm.subtaskId) || null
        : null;
    return {
      kind,
      parent,
      subtask,
      label: (subtask ? subtask.text : parent.title) || "",
      caseId: parent.caseId ?? null,
      ccId: parent.ccId ?? null,
      type: parent.type || "",
    };
  };
  if (tm.taskId != null) {
    const task = (tasks || []).find((x) => x && x.id === tm.taskId);
    return task ? pick("task", task) : null;
  }
  if (tm.eventId != null) {
    const ev = (agendaEvents || []).find((x) => x && x.id === tm.eventId);
    return ev ? pick("event", ev) : null;
  }
  return null;
}

/** The React key for a timer row. `tm.taskId` alone was the key at two sites and
 *  it was ALREADY a collision there: two subtask timers on one task are two rows
 *  with one key. `(parent, subtask)` is the pair the engine itself matches on. */
export const timerKey = (tm: TimerRow) =>
  `${tm.taskId ?? "e" + (tm.eventId ?? "")}:${tm.subtaskId ?? ""}`;

/**
 * BRIEF 116 §3 — THE ENGINE'S KEY IS A TRIPLE NOW, in one predicate.
 *
 * `activeTimers` was matched on `(taskId, subtaskId)` at four separate sites
 * (`_hasKey`, the resume `findIndex`, `setActiveTimer`, `pauseTimer`). That pair
 * was sufficient while every timer named a task or a subtask: 91 §3a's event
 * SUBTASK timer keys `(null, S)` and subtask ids come from one sequence, so it
 * stayed unique. AN EVENT'S OWN TIMER NAMES NEITHER — it keys `(null, null)` —
 * so a second event's timer would have matched the first one's row and resumed
 * somebody else's clock. Adding the third field at four sites by hand is how
 * three of them end up agreeing and the fourth does not; this is the predicate.
 */
export const sameTimer = (
  a: TimerRow | null | undefined,
  b: TimerRow | null | undefined,
) =>
  !!a &&
  !!b &&
  (a.taskId ?? null) === (b.taskId ?? null) &&
  (a.eventId ?? null) === (b.eventId ?? null) &&
  (a.subtaskId ?? null) === (b.subtaskId ?? null);
